import { supabase } from '@/lib/supabase';
import type { WebhookEndpoint, WebhookEvent, CreatedWebhook } from './types';

const SELECT_COLUMNS =
  'id, url, events, active, last_success_at, last_failure_at, failure_count, created_at, updated_at';

/**
 * Generate a webhook signing secret on the client.
 *
 * The secret stored in webhook_endpoint must be plaintext because the
 * dispatcher needs it to compute HMAC-SHA256 signatures at delivery time.
 * Whether it's generated here (browser crypto.getRandomValues) or in the
 * Postgres RPC (extensions.gen_random_bytes) is equivalent in practice —
 * both pull from a CSPRNG. Doing it client-side avoids another migration.
 */
function generateSecret(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `whsec_${hex}`;
}

export async function listMyWebhooks(): Promise<WebhookEndpoint[]> {
  const { data, error } = await supabase
    .from('webhook_endpoint')
    .select(SELECT_COLUMNS)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as WebhookEndpoint[];
}

export async function createWebhook(input: {
  url: string;
  events: WebhookEvent[];
}): Promise<CreatedWebhook> {
  const secret = generateSecret();

  // pessoa_id derives from the same SECDEF function the RLS WITH CHECK uses
  // (public.get_current_pessoa_id), so this path and api.webhook_create
  // converge on a single source of truth for ownership — no email lookup,
  // no chance of a stale reverse-mapping diverging from the policy.
  const { data: pessoaId, error: pessoaError } = await supabase
    .rpc('get_current_pessoa_id');
  if (pessoaError) throw pessoaError;
  if (!pessoaId) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('webhook_endpoint')
    .insert({
      url: input.url,
      events: input.events,
      secret,
      pessoa_id: pessoaId,
    })
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return { ...(data as WebhookEndpoint), secret };
}

export async function deleteWebhook(id: string): Promise<void> {
  const { error } = await supabase
    .from('webhook_endpoint')
    .delete()
    .eq('id', id);
  if (error) throw error;
}
